import { getCountryForSiteByLocale, getCurrencyForSiteByLocale } from "@config/site/site-config";
import { PRODUCT_IMAGE_PLACEHOLDER_URL } from "@ecco/ui/image-paths";
import type { SearchClient } from "algoliasearch";
import type { AlgoliaProduct, AlgoliaRelatedProduct, BrowseParams } from "./types";
import { getRelatedProductImageThumbnails } from "@components/commerce/product/right-panel/buy-card/utils";

export function normalizeAlgoliaProducts(products: AlgoliaProduct[]) {
    return products.map((hit) => ({
        ...hit,
        image: hit.image || PRODUCT_IMAGE_PLACEHOLDER_URL,
        images: hit.images?.length ? hit.images : [PRODUCT_IMAGE_PLACEHOLDER_URL],
        productColors:
            hit.productColors?.map((color) => ({
                ...color,
                priceWithDiscount: color.priceRaw,
                priceWithoutDiscount: color.listPriceRaw,
                image: color.image || PRODUCT_IMAGE_PLACEHOLDER_URL,
            })) ?? [],
    }));
}

export async function normalizedBrowseObjects(
    client: SearchClient,
    indexName: string,
    params: BrowseParams
): Promise<void> {
    const { batch, ...browseParams } = params;

    await client.browseObjects<AlgoliaProduct>({
        indexName,
        browseParams,
        // Algolia streams matching records page by page. We normalize each
        // page into our AlgoliaProduct shape and hand it to the caller's
        // callback, so callers depend on our contract, not the SDK's response.
        aggregator: ({ hits }) => {
            batch?.(normalizeAlgoliaProducts(hits));
        },
    });
}

export function normalizeRelatedProduct(algoliaProduct: AlgoliaProduct): AlgoliaRelatedProduct {
    return {
        variants:
            algoliaProduct?.variants?.map((v) => ({
                availableQuantity: v.availableQuantity,
                hasStock: v.hasStock,
                size: v.size,
                sku: v.sku,
            })) || [],
        categories: algoliaProduct?.categories || [],
        imageThumbnails: getRelatedProductImageThumbnails(algoliaProduct),
        eaBadge: algoliaProduct?.eaBadge || undefined, // Early access badge
        eccoClubBadge: algoliaProduct?.eccoClubBadge || undefined,
    };
}

function buildProductsIndexName(locale: string) {
    const env = process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT === "master" ? "prod" : "test";
    const country = getCountryForSiteByLocale(locale);
    const currency = getCurrencyForSiteByLocale(locale);
    return `${env.toLowerCase()}_${country}_${locale}_${currency}_Products`;
}

function buildStoresIndexName() {
    const env = process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT === "master" ? "prod" : "test";

    return `${env.toLowerCase()}_Stores`;
}

// Just string building, no SDK involved. Kept here instead of in either client file so
// callers that only need an index name don't have to import a client at all.
export const getAlgoliaIndexName = (locale: string, mode: "products" | "stores" = "products") =>
    mode === "stores" ? buildStoresIndexName() : buildProductsIndexName(locale);
